import { Input } from 'antd';
import BridgedDropdown from 'components/common/bridged-dropdown';
import type { InputProps, InputRef, MenuProps } from 'antd';
import { css } from '@emotion/react';
import { type Env0Theme } from 'constants/themes.constants';
import withTooltip from 'components/common/form-controlled/with-tooltip.hoc';
import { getCommonInputCssFromProps } from 'components/common/input-components/common-styles';
import { ErrorMessage } from 'components/common/form-components';
import React from 'react';
import isString from 'lodash/isString';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'types/theme.types';
import withLabel from 'components/common/form-controlled/with-label.hoc';

export interface TextInputProps extends InputProps {
  id?: string;
  name?: string;
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  placeholderValues?: Record<string, string>;
  disabled?: boolean;
  invalid?: boolean;
  error?: InputError;
  hideError?: boolean;
  hideEmptyError?: boolean;
  errorShouldNotTakeSpace?: boolean;
  'data-e2e': string;
  fullWidth?: boolean;
  grow?: boolean;
  menu?: MenuProps;
  addonColor?: string;
  ref?: React.Ref<InputRef>;
}

export type InputError = string | InputErrorExtended | null;
type InputErrorExtended = { id: string; values: Record<string, any> };

const TextInput = React.forwardRef<InputRef, TextInputProps>((props, ref) => {
  const {
    placeholder,
    placeholderValues,
    error,
    hideError,
    hideEmptyError,
    fullWidth,
    grow,
    menu,
    errorShouldNotTakeSpace,
    ...otherProps
  } = props as TextInputProps;
  const intl = useIntl();
  const translatedPlaceholder = placeholder && intl.formatMessage({ id: placeholder }, placeholderValues);

  const errorAsObj = error as InputErrorExtended;
  const errorId = !isString(error) ? errorAsObj?.id : error;
  const errorValues = errorAsObj?.values;

  const input = (
    <StyledInput
      {...otherProps}
      ref={ref}
      placeholder={translatedPlaceholder}
      autoComplete={menu ? 'off' : otherProps.autoComplete}
    />
  );

  return (
    <FlexColumn fullWidth={fullWidth} grow={grow}>
      {menu ? (
        <BridgedDropdown disabled={otherProps.disabled} menu={menu}>
          <span>{input}</span>
        </BridgedDropdown>
      ) : (
        input
      )}
      {!hideError && (
        <ErrorMessage
          data-e2e={otherProps['data-e2e'] ? `${otherProps['data-e2e']}-error` : null}
          errorShouldNotTakeSpace={errorShouldNotTakeSpace}
          hideWhenEmpty={hideEmptyError}>
          {errorId && <FormattedMessage id={errorId} values={errorValues} />}
        </ErrorMessage>
      )}
    </FlexColumn>
  );
});

TextInput.displayName = 'TextInput';

type StyledInputProps = TextInputProps & { theme: Env0Theme };

// Sizing and look come from the antd Input block in `antd.constants`.
const StyledInput = styled(Input)<TextInputProps>`
  ${({ invalid, error, theme }) => getCommonInputCssFromProps({ theme, invalid: invalid || Boolean(error) })}

  ${(props: StyledInputProps) =>
    props.addonColor &&
    css`
      /* Provider brand colour on the addon is meaning, not styling, so it is not a theme token. */
      &.ant-input-group-wrapper .ant-input-group-addon {
        color: ${props.addonColor};
      }
    `}
`;

const FlexColumn = styled.div<{ fullWidth?: boolean; grow?: boolean }>`
  display: flex;
  flex-direction: column;
  ${({ fullWidth }) => (fullWidth ? 'width: 100%;' : '')}
  ${({ grow }) => (grow ? 'flex-grow: 1' : '')}
`;

export const TextInputWithLabel = withLabel(TextInput);
export const TextInputWithTooltipAndLabel = withLabel(withTooltip(TextInput));
export default TextInput;
